import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FlightTicketPropType } from 'PropTypes';
import { Col, Row, Button, Modal, Card, Badge } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import StatusStaffTabsRow from 'Components/StatusStaffTabsRow';
import FlightCard from 'Views/TicketProfile/Components/FlightCard';
import { DS_F2W_Business_Travel_Team } from 'Constants/F2wRolesConstants';

const BusinessTravelTeamTab = (props) => {
  const { flyToWorkTicketInfo, setRefreshComponent, isLockRequired, resetLockTimers } = props;

  const [flightTotals, setFlightTotals] = useState({});
  const GBP_TO_EUR = 1.2;

  const handleChildTotalChange = useCallback((flightId, total, currency) => {
    setFlightTotals((prev) => {
      if (!flightId && flightId !== 0) return prev;
      const idKey = String(flightId);
      const nextTotal = Number(total) || 0;
      const nextCurrency = (currency || '').toUpperCase();
      const prevEntry = prev[idKey];
      if (prevEntry && prevEntry.total === nextTotal && prevEntry.currency === nextCurrency) return prev;

      const newTotals = { ...prev, [idKey]: { total: nextTotal, currency: nextCurrency } };

      // Force immediate update of the total display
      return newTotals;
    });
  }, []);

  // Force recalculation of totals when flights data changes
  useEffect(() => {
    const currentFlights = (flyToWorkTicketInfo.flights || []).filter((f) => !!f?.bookingReference);

    // Update totals based on current flight data
    const newTotals = {};
    currentFlights.forEach((flight) => {
      if (flight.id) {
        const flightTotal =
          (Number(flight.xbagCost) || 0) +
          (Number(flight.flightCost) || 0) +
          (Number(flight.otherTransportCost) || 0) +
          (Number(flight.hotelCost) || 0);

        newTotals[String(flight.id)] = {
          total: flightTotal,
          currency: (flight.currency || '').toUpperCase(),
        };
      }
    });

    setFlightTotals(newTotals);
  }, [flyToWorkTicketInfo.flights]);

  const convertToEur = (amount, currency) => {
    if ((currency || '').toUpperCase() === 'GBP') return (Number(amount) || 0) * GBP_TO_EUR;
    return Number(amount) || 0; // treat non-GBP as already EUR
  };

  const totalCostEur = useMemo(
    () =>
      Object.values(flightTotals).reduce((acc, entry) => {
        if (!entry) return acc;
        return acc + convertToEur(entry.total, entry.currency);
      }, 0),
    [flightTotals]
  );

  // Get current user and their roles from Redux store
  const user = useSelector((state) => state.currentUser);
  const userRoles = user?.roles || [];

  // State to control the visibility of the Add Flight modal
  const [showAddFlightModal, setShowAddFlightModal] = React.useState(false);

  // Function to determine if the "Add flight to this ticket" button should be disabled
  const isInsertFlightTicketDisabled = () => {
    let result = true;
    if (userRoles.includes(DS_F2W_Business_Travel_Team) && flyToWorkTicketInfo.status?.toLowerCase() === 'pendingbtt') {
      result = false;
    }
    return result;
  };

  const flightsWithBooking = flyToWorkTicketInfo.flights?.filter((f) => f.bookingReference) || [];

  return (
    <React.Fragment>
      <StatusStaffTabsRow flyToWorkTicketInfo={flyToWorkTicketInfo} />
      <hr className="mt-0" />

      {/* Total Cost Summary Card */}
      <Row className="mb-3">
        <Col>
          <Card className="border-primary">
            <Card.Body className="d-flex justify-content-between align-items-center py-2">
              <div>
                <i className="fa fa-calculator me-2 text-primary"></i>
                <strong>Total Cost (EUR)</strong>
              </div>
              <Badge bg="primary" className="fs-6 px-3 py-2">
                €{totalCostEur.toFixed(2)}
              </Badge>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          {/* Add Flight Button */}
          {!isInsertFlightTicketDisabled() && (
            <Button variant="primary" size="sm" className="w-100 mb-3" onClick={() => setShowAddFlightModal(true)}>
              <i className="fa fa-plus me-2"></i>
              Add Flight to Ticket
            </Button>
          )}

          {/* Flights List */}
          {flightsWithBooking.length > 0 ? (
            flightsWithBooking.map((flight, key) => {
              flight.isSent = true;
              return (
                <FlightCard
                  key={flight.id ?? key}
                  flight={flight}
                  flyToWorkTicketInfo={flyToWorkTicketInfo}
                  setRefreshComponent={setRefreshComponent}
                  setShowAddFlightModal={setShowAddFlightModal}
                  onTotalChange={handleChildTotalChange}
                  isLockRequired={isLockRequired}
                  resetLockTimers={resetLockTimers}
                />
              );
            })
          ) : (
            <Card className="text-center py-5">
              <Card.Body>
                <i className="fa fa-plane fa-3x text-muted mb-3"></i>
                <p className="text-muted mb-0">No flights added yet</p>
                <p className="text-muted small">Click the button above to add a flight</p>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      {/* Modal to add a new flight */}
      <Modal
        size="lg"
        show={showAddFlightModal}
        onHide={() => setShowAddFlightModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fa fa-plane me-2"></i>
            Add New Flight
          </Modal.Title>
        </Modal.Header>
        <FlightCard
          flyToWorkTicketInfo={flyToWorkTicketInfo}
          flight={{
            confirmedFlightDate: '',
            bookingReference: '',
            flightNumber: '',
            via: '',
            departureAirport: '',
            flightDepartureTime: '',
            arrivalAirport: '',
            flightArrivalTime: '',
            travelType: '',
            paymentMethod: '',
            luggage: '',
            xbagCost: '',
            flightCost: '',
            hotelCost: '',
            costCentre: '',
          }}
          setRefreshComponent={setRefreshComponent}
          setShowAddFlightModal={setShowAddFlightModal}
          showCloseButtonOnModal={true}
          isLockRequired={isLockRequired}
          resetLockTimers={resetLockTimers}
        />
      </Modal>
    </React.Fragment>
  );
};

BusinessTravelTeamTab.propTypes = {
  flyToWorkTicketInfo: FlightTicketPropType.isRequired,
  setRefreshComponent: PropTypes.func,
  refreshComponent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isLockRequired: PropTypes.bool,
  resetLockTimers: PropTypes.func,
};

export default BusinessTravelTeamTab;
